import React from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import styled from 'styled-components';
import {useTranslations} from "use-intl";

import { FeedbackFormPayload, sendFeedbackForm } from '@/services/requests';
import Button from '@/components/Button';
import { TextInputFormik } from '@/components/TextInput';
import { TextAreaFormik } from '@/components/TextArea';
import {
  areAllErrorsVisible,
  validatePhone,
} from '@/components/TextInput/formikHelpers/form.helpers';
import UseModalsOpen from '@/hooks/useModalsOpen';
import {TFunction} from "@/typings/common";

function validate(t:TFunction, { phone, name }: FeedbackFormPayload) {
  const errors: Partial<FeedbackFormPayload> = {};
  if (!validatePhone(phone)) {
    errors.phone = t('forms.phone_error');
  }
  if (!phone) {
    errors.phone = t('forms.required');
  }
  if (!name) {
    errors.name = t('forms.required');
  }
  return errors;
}

const hasFrontendValidationErrors = (t:TFunction, values: FeedbackFormPayload) =>
  !!Object.keys(validate(t, values)).length;

function FeedbackRequest({ setFormClosed }: { setFormClosed: () => void }) {
  const { closeModalState } = UseModalsOpen();

  const t = useTranslations();

  function handleSubmit(
    values: FeedbackFormPayload,
    formikHelpers: FormikHelpers<FeedbackFormPayload>
  ) {
    return sendFeedbackForm(values)
      .then(() => {
        setFormClosed();
        closeModalState();
        formikHelpers.setSubmitting(true);
      })
      .catch(() => {
        formikHelpers.setErrors({
          message: t('forms.unknown_error'),
        });
        formikHelpers.setStatus({
          hasBackendError: true,
        });
      })
      .finally(() => {
        formikHelpers.setSubmitting(false);
      });
  }
  return (
    <Body>
      <Text>{t('leave_contacts')}</Text>
      <Formik<FeedbackFormPayload>
        initialValues={{ name: '', phone: '', message: '' }}
        onSubmit={handleSubmit}
        validate={fields => validate(t, fields)}
      >
        {({ isSubmitting, isValid, values, dirty, ...props }) => (
          <Form>
            <FormRow>
              <TextInputFormik
                name="name"
                icon="profile"
                placeholder={t('forms.name')}
                invalid={false}
                autoComplete="off"
              />
            </FormRow>
            <FormRow>
              <TextInputFormik
                name="phone"
                icon="phone"
                placeholder={t('forms.phone')}
                invalid={false}
                autoComplete="off"
              />
            </FormRow>
            <FormRow>
              <StyledTextArea
                placeholder={t('forms.message')}
                name="message"
                invalid={false}
              />
            </FormRow>
            <Button
              type="submit"
              variant="contained-primary-w100-f13"
              disabled={
                (!isValid || hasFrontendValidationErrors(t, values)) &&
                areAllErrorsVisible(props.errors, props.touched)
              }
              children={t('forms.send')}
              loading={isSubmitting}
            />
          </Form>
        )}
      </Formik>
    </Body>
  );
}

export default FeedbackRequest;

const Text = styled.div`
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 20px;
`;

const FormRow = styled.div`
  margin-bottom: 30px;
`;

const Body = styled.div`
  margin-top: 20px;
  padding: 0 15px;
`;

const StyledTextArea = styled(TextAreaFormik)`
  textarea {
    height: 160px;
  }
`;
